import React from "react";
import { ChevronRight, Sparkles, Zap, Shield } from "lucide-react";
import { Button } from "./ui/button";

const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-36 md:pt-40 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1729944950511-e9c71556cfd4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxVViUyMHByaW50aW5nfGVufDB8fHx8MTc2MTU5MjQwMnww&ixlib=rb-4.1.0&q=85"
          alt="Industrial Printing Technology"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/85"></div>
      </div>

      {/* Geometric Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#8B1538] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#DC143C] rounded-full blur-3xl"></div>
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2UwZTBlMCIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30"></div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in-up">
            {/* Slogan */}
            <div className="mb-4">
              <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#8B1538] via-[#DC143C] to-[#FF6B9D] bg-clip-text text-transparent italic">
                "Adding colors to life"
              </h3>
            </div>

            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md border border-red-100">
              <img src="/quality-logo.png" alt="ISO 9001:2015" className="h-6 w-6" />
              <span className="text-sm font-semibold text-gray-700">ISO 9001:2015 Certified</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight text-gray-900">
              Complete solutions
              <span className="block text-gradient">UV / LED coatings...!</span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
              Leading manufacturer of advanced UV and LED curable coatings for offset, screen, gravure, and flexo printing processes. Delivering innovation since 2000.
            </p>

            {/* Key Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <Zap className="h-8 w-8 text-[#8B1538]" />
                <div>
                  <div className="font-bold text-gray-900">Instant Curing</div>
                  <div className="text-sm text-gray-600">UV/LED Tech</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <Shield className="h-8 w-8 text-[#8B1538]" />
                <div>
                  <div className="font-bold text-gray-900">Low VOC</div>
                  <div className="text-sm text-gray-600">Eco-Friendly</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <Sparkles className="h-8 w-8 text-[#8B1538]" />
                <div>
                  <div className="font-bold text-gray-900">Custom</div>
                  <div className="text-sm text-gray-600">Formulations</div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-[#8B1538] hover:bg-[#DC143C] text-white px-8 py-6 text-lg rounded-md font-semibold"
              >
                Get Custom Solutions
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => scrollToSection('coatings')}
                variant="outline"
                className="border-2 border-[#8B1538] text-[#8B1538] hover:bg-red-50 px-8 py-6 text-lg rounded-md font-semibold"
              >
                Explore Products
              </Button>
            </div>
          </div>

          {/* Right Content - Brochure Preview */}
          <div className="relative animate-float">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src="/crimson-brochure-new.png"
                alt="Crimson Inks Product Brochure"
                className="w-full h-auto object-cover"
              />
            </div>
            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#8B1538] rounded-full opacity-20 blur-2xl"></div>
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#DC143C] rounded-full opacity-20 blur-2xl"></div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-gray-400 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
